import Model, { attr, hasMany } from '@ember-data/model';
import { cached } from '@glimmer/tracking';
import { type Registry as Services, service } from '@ember/service';
import type { ModelRegistry } from 'ember-data/types/registries/model';
import {
  OccasionDayOfWeek,
  OccasionEvent,
  OccasionPreposition,
  OccasionTermUnit,
} from 'uplisting-frontend/models/occasion';
import type AutomatedMessageRuleFilterModel from 'uplisting-frontend/models/automated-message-rule-filter';
import type ChannelModel from 'uplisting-frontend/models/channel';

export default class AutomatedMessageRuleModel extends Model {
  @service intl!: Services['intl'];

  @attr('string') name!: string;
  @attr('string', { defaultValue: '' }) content!: string;
  @attr('string') description!: string;
  @attr('string') time!: string | null;
  @attr('number') term!: number;
  @attr('boolean') enabled!: boolean;
  @attr('boolean') lastMinute!: boolean;
  @attr('string', { defaultValue: OccasionEvent.arrived })
  event!: OccasionEvent;

  @attr('string', { defaultValue: OccasionPreposition.before })
  preposition!: OccasionPreposition;

  @attr('string', { defaultValue: OccasionTermUnit.hours })
  termUnit!: OccasionTermUnit;

  @attr('string') dayOfWeek!: OccasionDayOfWeek | null;

  @hasMany('property', { inverse: null, async: false })
  properties!: ModelRegistry['property'][];

  @hasMany('automated-message-rule-filter', {
    inverse: 'automatedMessageRule',
    async: false,
  })
  automatedMessageRuleFilters!: AutomatedMessageRuleFilterModel[];

  @hasMany('channel', { inverse: null, async: false })
  channels!: ChannelModel[];

  @cached
  get order(): number {
    if (!this.id) {
      return 1;
    }

    return this.primaryOrder + this.secondaryOrder;
  }

  @cached
  get primaryOrder(): number {
    switch (this.event) {
      case OccasionEvent.enquired:
        return 2;
      case OccasionEvent.booked:
        return 3;
      case OccasionEvent.arrived:
        if (this.preposition === OccasionPreposition.before) {
          return 4;
        }

        return 5;
      case OccasionEvent.departed:
        if (this.preposition === OccasionPreposition.before) {
          return 6;
        }

        return 7;
      case OccasionEvent.cancelled:
        return 8;
      case OccasionEvent.day_of_week:
        return 9;
    }
  }

  @cached
  get secondaryOrder(): number {
    if (this.isRecurring) {
      const dayIndex =
        Object.values(OccasionDayOfWeek).indexOf(
          this.dayOfWeek as OccasionDayOfWeek,
        ) + 1;

      return dayIndex / 8;
    }

    const order = 1 / this.termInMinutes;

    if (this.preposition === OccasionPreposition.before) {
      return order;
    }

    return 1 - order;
  }

  @cached
  get isRecurring(): boolean {
    return this.event === OccasionEvent.day_of_week;
  }

  @cached
  get termInMinutes(): number {
    // term could be 0:
    // adding 1 to avoid division by zero
    // adding 1 more to avoid 1 as secondaryOrder
    const adjustedTerm = this.term + 2;

    switch (this.termUnit) {
      case OccasionTermUnit.minutes:
        return adjustedTerm;
      case OccasionTermUnit.hours:
        return adjustedTerm * 60;
      case OccasionTermUnit.days:
        return adjustedTerm * 1440;
    }
  }

  @cached
  get rulePrettified(): string {
    const { intl } = this;

    if (this.isRecurring) {
      return intl.t('outbound_messages_index.recurring_trigger', {
        day: intl.t(`outbound_messages_new.days.${this.dayOfWeek}`),
        time: this.time,
      });
    }

    const term = intl.t(`time_formatted.${this.termUnit}`, {
      count: this.term,
    });
    const preposition = intl.t(`preposition.${this.preposition}`);
    const event = intl.t(`event.${this.event}`);

    return `${term} ${preposition} ${event}`;
  }
}

declare module 'ember-data/types/registries/model' {
  interface ModelRegistry {
    'automated-message-rule': AutomatedMessageRuleModel;
  }
}
